import { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import 'ag-grid-community/styles/ag-grid.css'
import 'ag-grid-community/styles/ag-theme-balham.css'
import { FilterChangedEvent, GridApi } from 'ag-grid-community'
import 'ag-grid-enterprise'
import { useTheme } from '@mui/material/styles'
import { Card, CardHeader, Typography, CardContent } from '@mui/material'
import { exportMultipleSheetsAsExcel } from 'ag-grid-enterprise'
import { AgGridReact } from 'ag-grid-react'
import { constants } from '../../common/constants'
import { getRowStyleMuiAgGrid } from '../../common/commonFunctions'
import { pnlSecurityColDefs } from '../../common/columnPNLBySec'
import _ from 'lodash'

interface AgGridProps {
  title?: string
  rowData: any
  height?: any
  type?: string
  onClickHan?: any
  onClickSelectData?: any
  onCellChange?: any
  seTtotalSelectedRows?: any
  getBtnRef?: any
  getGridApi?: any
  doesExternalFilterPass?: any
  isExternalFilterPresent?: any
  isDeselectAll?: boolean
  getContextMenuItems?: any
  isSingleRowInserted?: boolean
  defaultFilter?: any
  isEODDashboard?: boolean
  refreshData?: any
}

const PNLBySecGrid = ({
  rowData,
  getGridApi,
  isDeselectAll,
  getContextMenuItems,
  defaultFilter,
  isEODDashboard,
  refreshData,
  getBtnRef
}: AgGridProps) => {
  const gridRef = useRef<any>(null)
  const theme = useTheme()
  const [globalSearch, setGlobalSearch] = useState<string>()
  const [gridTheme, setGridTheme] = useState<string>(
    theme.palette.mode === 'dark' ? ' ag-theme-balham-dark' : ' ag-theme-balham'
  )
  useEffect(() => {
    setGridTheme(theme.palette.mode === 'dark' ? ' ag-theme-balham-dark' : ' ag-theme-balham')
  }, [theme.palette.mode])
  const [columnDefs] = useState<any>(pnlSecurityColDefs)
  let gridApi: any
  const defaultColDef = useMemo<any>(() => {
    return {
      flex: 1,
      sortable: true,
      filter: true,
      floatingFilter: false,
      suppressFilterButton: false,
      editable: false,
      resizable: true,
      headerCheckboxSelectionFilteredOnly: true,
      menuTabs: [constants.FilterMenuTab],
      tooltipShowDelay: 10,
      tooltipMouseTrack: true,
      suppressKeyboardEvent: (params: any) => {
        const isTabKey = params?.event?.key === constants.Tab
        if (isTabKey) gridRef?.current?.api?.stopEditing()

        return false
      },
      headerClass: function() {
        return 'ag-grid-mui-header';
      }
    }
  }, [])

  useEffect(() => {
    gridApi = gridRef?.current?.api

    const gridColumnApi = gridRef?.current?.columnApi
    getUpdatedData()
    getName(gridApi, gridColumnApi)
    getBtnRef(gridRef);
  }, [rowData])

  const getUpdatedData = useCallback(async () => {
    await gridApi?.applyTransactionAsync({ update: [rowData] })
    gridApi?.refreshCells()
  }, [rowData])
  useEffect(() => {
    if (defaultFilter && gridRef) {
      const filterComponent = gridRef?.current?.api?.getFilterInstance(defaultFilter.column)
      if (filterComponent) {
        filterComponent?.setModel({ values: defaultFilter.values })
        gridApi.onFilterChanged()
      }
    }
  }, [gridRef])

  const onGridReady = (params: any) => {
    params.api.sizeColumnsToFit()
    gridApi = params.api

    const gridColumnApi = params.columnApi
    getName(gridApi, gridColumnApi)

    // Emmits the gridApi refrence to the parent component
    if (getGridApi) {
      getGridApi(gridApi, gridColumnApi)
    }
  }

  const getName = (api: any, columnApi: any) => {
    const rows: any = {}
    const result = []
    columnApi?.getAllGridColumns().forEach((item: any) => {
      rows[item.colId] = null
    })
    const columnsWithAggregation = [
      'borr_qty',
      'borr_bal',
      'borr_contracts',
      'loan_qty',
      'loan_bal',
      'loan_contracts',
      'excess_deficit_bal',
      'pnl'
    ]
    columnsWithAggregation.forEach(element => {
      api?.forEachNodeAfterFilter((rowNode: any) => {
        if (rowNode.data[element]) {
          rows[element] += Number(parseFloat(rowNode.data[element]))
        }
      })
    })

    columnsWithAggregation.forEach(element => {
      rows[element] = _.isNull(rows[element])?0:rows[element]
    })

    rows['isTotalLabel'] = true
    rows['ticker'] = countUnique('ticker', api)
    rows['cusip'] = countUnique('cusip', api)
    result.push(rows)
    api?.setPinnedBottomRowData(result)
  }

  /**
   * Method counts the unique value for given attribute (for filtered values also)
   * @param attribute
   * @returns unique values count
   */
  const countUnique = (attribute: string, api: GridApi): number => {
    const uniqueItemsSet: Set<any> = new Set()
    api?.forEachNodeAfterFilter((rowNode: any) => {
      if (rowNode?.data instanceof Object && attribute in rowNode.data && !_.isEmpty(rowNode.data[attribute])) {
        uniqueItemsSet.add(rowNode.data[attribute])
      }
    })

    return uniqueItemsSet.size
  }

  useEffect(() => {
    if (isDeselectAll != undefined) gridRef.current?.api?.deselectAll()
  }, [isDeselectAll])

  const rowClassRules = useMemo(() => {
    return {
      'is-new-warning': (params: any) => {
        const isNew = params.data.is_new

        return isNew === 'TRUE'
      },
      'is-new-recall': (params: any) => {
        const isNew = params.data.is_Recall

        return isNew === 'True'
      }
    }
  }, [])

  const autoGroupColumnDef = useMemo(() => {
    return {
      minWidth: 100,
      maxWidth: 200
    }
  }, [])

  const getRowId = (params: any) => {
    if (params && params?.data) {
      if (params?.data?.id) {
        return params?.data?.id
      }
    }
    else
     return makeid(5)
  }

  function makeid(length: any) {
    let result = ''
    const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789'
    const charactersLength = characters.length
    let counter = 0
    while (counter < length) {
      result += characters.charAt(Math.floor(Math.random() * charactersLength))
      counter += 1
    }

    return result
  }

  const sideBar = useMemo(() => {
    return {
      toolPanels: [
        {
          id: 'columns',
          labelDefault: 'Columns',
          labelKey: 'columns',
          iconKey: 'columns',
          toolPanel: 'agColumnsToolPanel',
          toolPanelParams: {
            suppressRowGroups: true,
            suppressValues: true,
            suppressPivots: true,
            suppressPivotMode: true,
            suppressColumnFilter: false,
            suppressColumnSelectAll: false,
            suppressColumnExpandAll: true
          }
        }
      ],
      defaultToolPanel: ''
    }
  }, [])

  function isForceRefreshSelected() {
    return (document.querySelector('#forceRefresh') as HTMLInputElement)?.checked
  }
  function isSuppressFlashSelected() {
    return (document.querySelector('#suppressFlash') as HTMLInputElement)?.checked
  }
  const onGridRowDataChanged = () => {
    const refreshParams: any = {
      force: isForceRefreshSelected(),
      suppressFlash: isSuppressFlashSelected()
    }
    gridRef.current!.api.refreshCells(refreshParams)
  }

  const refresh = () => {
    refreshData?.()
  }

  const onExcelExport = () => {
    const spreadsheets = []
    spreadsheets.push(
      gridRef?.current?.api?.getSheetDataForExcel?.({
        sheetName: 'P&L Summary'
      })
    )

    exportMultipleSheetsAsExcel({
      data: spreadsheets,
      fileName: 'counter-party-exposure.xlsx'
    })
  }

  const onFilterChanged = (params: FilterChangedEvent) => {
    const { api, columnApi } = params
    getName(api, columnApi)
  }

  return (
    <Card className='h-100 w-100 p-0'>
      <CardHeader
        className='p-0 ps-2'
        subheader={
          isEODDashboard ? (
            <Typography className='title-subHeaderTitle ps-0' style={{ height: "4%", color: "#1E355E" }}>
              PNL by Security
            </Typography>
          ) : (
            ''
          )
        }
      ></CardHeader>
      <CardContent className='w-100 h-100 p-0 px-1'>
        {!isEODDashboard && (
          <div id='cpty-grid-title' className='row'>
            <div className='col '>
              <div className='row mb-1'>
                <div className='col-12 d-flex align-items-center'>
                  {/* <GlobalSearch setGlobalSearch={setGlobalSearch} /> */}
                </div>
              </div>
            </div>
            <div className='col d-flex justify-content-end align-items-center'>
              {/* <DownloadRefresh downloadEvent={onExcelExport} refreshEvent={refresh} /> */}
            </div>
          </div>
        )}
        <div className={`row` + gridTheme} style={{  height: window?.innerWidth > 992 ? "94%" : "50vh", borderRadius: 5 }}>
          <AgGridReact
            ref={gridRef}
            defaultColDef={defaultColDef}
            rowStyle={{
              justifyContent: 'center',
              fontSize: '0.75rem',
              fontWeight: '400',
              fontStyle: 'normal',
              color:'rgba(0, 0, 0, 0.7)',
              fonFamily:"Roboto, Helvetica, Arial, sans-serif"
            }}
            
            rowHeight={28}
            getRowStyle={getRowStyleMuiAgGrid}
            headerHeight={32}
            columnDefs={columnDefs}
            getRowId={getRowId}
            onRowDataUpdated={onGridRowDataChanged}
            rowData={rowData}
            suppressRowClickSelection={true}
            suppressAggFuncInHeader={true}
            enableRangeSelection={true}
            onGridReady={onGridReady}
            rowClassRules={rowClassRules}
            autoGroupColumnDef={autoGroupColumnDef}
            groupSelectsChildren={true}
            groupSelectsFiltered={true}
            sideBar={sideBar}
            animateRows={true}
            pivotMode={false}
            cacheQuickFilter={true}
            overlayLoadingTemplate={
              '<span class="ag-overlay-loading-center">Please wait while your rows are loading</span>'
            }
            getContextMenuItems={getContextMenuItems}
            quickFilterText={globalSearch}
            onFilterChanged={onFilterChanged}
          ></AgGridReact>
        </div>
      </CardContent>
    </Card>
  )
}

export default PNLBySecGrid
